import { inject, Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import { TestsService } from "../../../api/services";
import {
  CreateTestDto,
  FilterTestDto,
  ResponseTestDto,
  UpdateTestDto,
} from "../../../api/models";
import { Observable } from "rxjs";

const TENANT = environment.tenant;

@Injectable({
  providedIn: "root",
})
export class TestsApiService {
  /** The tests service */
  private readonly _testsService: TestsService = inject(TestsService);

  /**
   * Creates a new test.
   * @param test The test to create.
   * @returns The created test.
   */
  public create$(body: CreateTestDto): Observable<ResponseTestDto> {
    return this._testsService.testsControllerCreate({ body });
  }

  /**
   * Updates a test.
   * @param id The id of the test to update.
   * @param body The test to update.
   * @returns The updated test.
   */
  public update$(
    id: string,
    body: UpdateTestDto,
  ): Observable<ResponseTestDto> {
    return this._testsService.testsControllerUpdate({
      tenant: TENANT,
      id,
      body,
    });
  }

  /**
   * Deletes a test.
   * @param id The id of the test to delete.
   * @returns The deleted test.
   */
  public delete$(id: string): Observable<void> {
    return this._testsService.testsControllerDelete({
      tenant: TENANT,
      id,
    });
  }

  /**
   * Retrieves a test.
   * @param id The id of the test to retrieve.
   * @returns The retrieved test.
   */
  public findOne$(id: string): Observable<ResponseTestDto> {
    return this._testsService.testsControllerFindOne({
      tenant: TENANT,
      id,
    });
  }

  /**
   * Filters tests.
   * @param filterDto The filter DTO.
   * @returns The filtered tests.
   */
  public filter$(
    filterDto: Partial<FilterTestDto>,
  ): Observable<ResponseTestDto[]> {
    const body: FilterTestDto = {
      ...filterDto,
      tenant: TENANT,
    };

    return this._testsService.testsControllerFilter({
      body,
    });
  }
}
